import { isSvg } from "lib/isSvg";

const DANGEROUS_ELEMENTS = [
  "script",
  "foreignobject",
  "iframe",
  "object",
  "embed"
];

const SAFE_HREF_PROTOCOLS = [
  "http:",
  "https:",
  "data:image/png",
  "data:image/jpeg",
  "data:image/gif"
];

interface ValidationResult {
  valid: boolean;
  error?: string;
}

export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ({ target }) => resolve(target?.result as string);
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsText(file);
  });
};

export const validateSvgContent = (content: string): ValidationResult => {
  if (!content || !content.trim()) {
    return { valid: false, error: "The uploaded file is empty." };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "image/svg+xml");

  // DOMParser returns a document with a <parsererror> element if XML is invalid
  // Check by namespace to avoid false positives on SVGs containing a literal <parsererror> element
  const parserErrors = doc.getElementsByTagNameNS(
    "http://www.mozilla.org/newlayout/xml/parsererror.xml",
    "parsererror"
  );
  if (parserErrors.length > 0) {
    return {
      valid: false,
      error: "The uploaded file is not a valid SVG."
    };
  }
  // Fallback for jsdom and other environments that don't use the Mozilla namespace
  const genericParserError = doc.querySelector("parsererror");
  if (genericParserError) {
    return {
      valid: false,
      error: "The uploaded file is not a valid SVG."
    };
  }

  // Verify the root element is <svg> (handle namespace-prefixed variants like svg:svg)
  const root = doc.documentElement;
  const rootTag = root.tagName.toLowerCase();
  if (rootTag !== "svg" && root.localName?.toLowerCase() !== "svg") {
    return {
      valid: false,
      error: "The uploaded file is not a valid SVG."
    };
  }

  const unsafeError =
    "This SVG contains unsafe content and cannot be uploaded. Please remove any scripts or embedded objects and try again.";

  // Check all elements for dangerous tags, attributes, and href values
  const allElements = Array.from(doc.getElementsByTagName("*"));

  for (const el of allElements) {
    // Dangerous tag check
    if (DANGEROUS_ELEMENTS.includes(el.tagName.toLowerCase())) {
      return { valid: false, error: unsafeError };
    }

    // Event handler attribute check
    for (let j = 0; j < el.attributes.length; j++) {
      const attrItem = el.attributes.item(j);
      if (attrItem && attrItem.name.toLowerCase().startsWith("on")) {
        return { valid: false, error: unsafeError };
      }
    }

    // Href protocol allowlist check — reject anything not explicitly safe
    for (const attr of ["href", "xlink:href"]) {
      const value = el.getAttribute(attr);
      if (value) {
        const trimmed = value.trim().toLowerCase();
        // Allow fragment refs (#id) and relative URLs
        if (trimmed.startsWith("#") || !trimmed.includes(":")) {
          continue;
        }
        const isSafe = SAFE_HREF_PROTOCOLS.some(protocol =>
          trimmed.startsWith(protocol)
        );
        if (!isSafe) {
          return { valid: false, error: unsafeError };
        }
      }
    }
  }

  return { valid: true };
};

export const validateSvgFile = async (
  file: File
): Promise<ValidationResult> => {
  const content = await readFileAsText(file);
  return validateSvgContent(content);
};

export const validateSvgOrReject = async (
  file: File
): Promise<{ ok: true } | { ok: false; error: string }> => {
  if (!isSvg(file)) return { ok: true };
  const { valid, error } = await validateSvgFile(file);
  return valid ? { ok: true } : { ok: false, error: error! };
};
